import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, QueryList, Renderer2, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Chart, ChartConfiguration, ChartDataset, ChartOptions, ChartType, LegendItem, Plugin, registerables } from 'chart.js';
import { ChoroplethController, IGeoDataPoint, ProjectionScale, ColorScale, GeoFeature, topojson } from '@lacour/chartjs-chart-geo'
import { ForceDirectedGraphController, DendogramController, EdgeLine, RectLabelElement, TreeController, GraphController, DendrogramController, IGraphChartOptions } from '@lacour/chartjs-chart-pathflow';
import europe from 'src/assets/stats/europe.json'
import { ChartContent, ChartDataset as ChartDataset2 } from 'src/app/entities/ChartContent';
import { MatPaginatorIntlTranslated } from 'src/app/tools/paginator/paginator';
import { IPays, IxellAPIService, StatsGraphData, StatsGraphDataBrowsingNode, StatsGraphShape } from 'src/app/tools/api.service';
import { Utils } from 'src/app/tools/utils';
import { LegendStatsDirective } from '../statistics/legend/legend.directive';
import { AfterContentInit } from '@angular/core';
import { StorageService } from '../tools/storage.service';
import { Sort } from '@angular/material/sort';
import { Constants } from '../entities/Constants';
import ExcellentExport, { ConvertOptions, SheetOptions } from 'excellentexport';
import { FormatDefinition } from 'excellentexport/dist/format';

Chart.register(...registerables);
Chart.register(ChoroplethController, GeoFeature, ColorScale, ProjectionScale);
Chart.register(ForceDirectedGraphController, DendogramController, TreeController, GraphController, EdgeLine, RectLabelElement/*, LinearScale, PointElement*/);

interface TableElement {
  date: string;
  key: string;
  name: string;
  value: number | string;
}

interface LegendItemExp {
  val: LegendItem;
  chart: Chart;
}

@Component({
  selector: 'mg-chart',
  templateUrl: './mg-chart.component.html',
  styleUrls: ['./mg-chart.component.scss'],
  providers: [
    {
      provide: MatPaginatorIntl, deps: [TranslateService], useFactory: (translateService: TranslateService) => new MatPaginatorIntlTranslated(translateService)
    }
  ]
})

export class DiagramComponent implements OnChanges {
  @Input() uid: number;
  @Input() viewShape: StatsGraphShape;
  @Input() title: string;
  @Output() editValues = new EventEmitter<void>();
  @Output() shapeChange = new EventEmitter<StatsGraphShape>();
  @ViewChildren('canvas') canvases: QueryList<ElementRef>;
  @ViewChildren('legendItemID') legendItemID: QueryList<ElementRef>;
  @ViewChild('legendContainer') legendContainer: ElementRef;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  StatsGraphShape = StatsGraphShape;
  Utils = Utils;
  Date = Date;
  Object = Object;

  charts: Chart[] = [];

  largeSel: boolean;
  indexSel: boolean;
  stackBar: boolean;

  loading: boolean = false;

  displayedColumns: string[] = ['date', 'key', 'name', 'value'];
  displayedArbDict: { [key: string]: string } = {};
  tableDataSource: MatTableDataSource<any> = null;

  legendItemsL: LegendItemExp[][] = [];
  legendItemsS: LegendItem[] = [];

  _content: ChartContent = null;
  get content(): ChartContent {
    return this._content;
  }
  set content(value: ChartContent) {
    if (value !== this._content) {
      this._content = value;
      this.chartChanged();
    }
  }

  canvasTbl = [];

  private translateSub: any = null;

  constructor(private cdRef: ChangeDetectorRef, private translate: TranslateService, private renderer: Renderer2, private apiService: IxellAPIService, private storage: StorageService) { }

  ngOnChanges(changes: SimpleChanges) {
    let vsC = changes['viewShape'];
    if (vsC) {
      this.viewShapeChanged(vsC.previousValue);
    }
  }

  ngAfterViewInit() {
    this.legendItemID.changes.subscribe(t => {
      this.setLegendHeight();
    });
  }

  setLegendHeight() {
    if (this.legendContainer && this.legendItemID && this.legendItemID.length > 0) {
      let domElement = this.legendContainer.nativeElement as HTMLElement;
      const m = parseInt(window.getComputedStyle(domElement).width, 10);
      let h: number = 0;

      while (h < 4096) {
        let x = 0;
        let y = 0;

        let maxCol = 0;
        for (let legendItem of this.legendItemID) {
          let domItem = legendItem.nativeElement as HTMLElement;

          let w = parseInt(window.getComputedStyle(domItem).width, 10) + parseInt(window.getComputedStyle(domItem).marginLeft, 10);
          let hh = parseInt(window.getComputedStyle(domItem).height, 10) + 0.2;

          while ((y+hh) >= h) {
            x += (maxCol <= 0) ? w : maxCol;
            y = 0;
            maxCol = 0;

            if (x > m)
              break;
          }

          if (x > m)
            break;

          maxCol = Math.max(maxCol, w);
          y += hh;

          if ((x + w) > m) {
            x += maxCol;
            y = 0;
            maxCol = 0;
            break;
          }
        }

        if (x > m)
          h++;
        else
          break;
      }

      domElement.style.height = h + "px";
    }
  }

  async viewShapeChanged(emitEvent: boolean) {
    await this.chartChanged();

    if (emitEvent)
      this.editValues.emit();

    if (this.legendContainer != undefined)
      (this.legendContainer.nativeElement as HTMLElement).style.height = "1px";
  }

  async chartChanged() {
    this.tableDataSource = null;
    this.cdRef.detectChanges();
    this.loading = false;

    if (this.content != null && this.content.config != null) {
      if (this.content.config.shapeOverride !== null) {
        let shape = this.content.config.shapeOverride;
        this.content.config.shapeOverride = null;
        this.shapeChange.emit(shape);
        return;
      }

      for (let chart of this.charts)
        if (chart != null)
          chart.destroy();

      this.charts = [];

      let selectedType: ChartType;
      switch (this.viewShape) {
        case StatsGraphShape.TABLE:
        {
          let arr: TableElement[] = [];

          let labels = this.content.config.data.tableLabels ?? this.content.config.data.labels;
          
          for (let labelID = 0; labelID < labels.length; labelID++) {
            let label: string = labels[labelID] as string;
            for (let dataset of this.content.config.data.datasets) {
              arr.push({ date: label, key: dataset.key, name: dataset.label, value: dataset.data[labelID] })
            }
          }

          this.tableDataSource = new MatTableDataSource<TableElement>(arr);
          this.tableDataSource.paginator = this.paginator;
          return;
        }
        case StatsGraphShape.ACTIONS:
        {
          let arr: TableElement[] = [];

          let actions = this.content.config.actions.sort((a, b) => b.date.localeCompare(a.date));

          let language = this.storage.get("actualLanguage");
          if (language == "" || language == null)
            language = "fr";

          for (let action of actions) {
            let timeAsNumber = Number(action.date);

            let timeAsDate = new Date(timeAsNumber * 1000);

            let date: string = timeAsDate.toLocaleDateString(language) + " " + timeAsDate.toLocaleTimeString(language);

            let hasV = action.label.includes(':');
            let valK: string = hasV ? action.label.split(':')[0] : action.label;
            let valV: string = hasV ? action.label.substr(valK.length+1) : "";
            

            arr.push({ date: date, key: '', name: action.user, value: this.translate.instant(valK) + (hasV ? (this.translate.instant("stats.common.colon") + valV) : "") });
          }

          this.tableDataSource = new MatTableDataSource<TableElement>(arr);
          this.tableDataSource.paginator = this.paginator;
          return;
        }
        case StatsGraphShape.ARBITRARYTABLE:
        {
          this.tableDataSource = new MatTableDataSource<any>(this.content.config.arbTbl);
          this.tableDataSource.paginator = null;
          this.displayedArbDict = this.content.config.arbNames;

          return;
        }
        case StatsGraphShape.LINES:
        {
          selectedType = 'line';
          break;
        }
        case StatsGraphShape.PIE:
        {
          selectedType = 'pie';
          break;
        }
        case StatsGraphShape.DONUT:
        {
          selectedType = 'doughnut';
          break;
        }
        case StatsGraphShape.MAP:
        {
          selectedType = 'choropleth';
          break;
        }
        case StatsGraphShape.HIST:
        {
          selectedType = 'bar';
          break;
        }
        case StatsGraphShape.BROWSING:
        {
          selectedType = 'tree';
          break;
        }
      }

      let perDatasetColors: string[] = null;
      let actualDatasets: ChartDataset2<any>[] = [];
      let labels: unknown[] = [];

      let nations: GeoJSON.Feature<GeoJSON.Geometry, TopoJSON.Properties>[] = null;

      let isPieOrDonut: boolean = (this.viewShape == StatsGraphShape.PIE || this.viewShape == StatsGraphShape.DONUT);
      let isMap: boolean = (this.viewShape == StatsGraphShape.MAP);
      let isTree: boolean = (this.viewShape == StatsGraphShape.BROWSING);
      if (isPieOrDonut) {
        let labelsS: string[] = [...new Set(this.content.config.data.datasets.map(d => d.label))];

        for (let dataset of this.content.config.data.datasets) {
          let potentialDataset: ChartDataset2<any>[] = actualDatasets.filter(d => d.label == dataset.key);
          let cdataset: ChartDataset2<any> = null;

          if (potentialDataset.length <= 0) {
            cdataset = { label: dataset.key, key: '', data: new Array<number>(labelsS.length).fill(0) };
            actualDatasets.push(cdataset);
          }
          else
            cdataset = potentialDataset[0]

          let total: number = (dataset.data as number[]).reduce((sum, current) => sum + current, 0);
          cdataset.data[labelsS.indexOf(dataset.label)] = total;
        }

        labelsS.forEach((value, index, array) => array[index] = this.translate.instant(value));
        labels = labelsS;
      }
      else if (isMap) {
        nations = topojson.feature(europe as any, europe.objects.collection as TopoJSON.GeometryCollection<TopoJSON.Properties>).features;
        let alpha3codes: string[] = [...new Set(nations.map((d) => d.properties.ADM0_A3_GB))];
        let pays: IPays[] = await this.apiService.getCountriesProperties(alpha3codes).toPromise();

        let labelsS: string[] = [...new Set(this.content.config.data.datasets.map(d => d.label))];

        for (let dataset of this.content.config.data.datasets) {
          let potentialDataset: ChartDataset2<any>[] = actualDatasets.filter(d => d.label == dataset.key);
          let cdataset: ChartDataset2<any> = null;

          if (potentialDataset.length <= 0) {
            let data = new Array(labelsS.length);
            for (let i = 0; i < data.length; i++)
              data[i] = { feature: null, value: 0 };

            cdataset = { label: dataset.key, key: '', data: data };
            actualDatasets.push(cdataset);
          }
          else
            cdataset = potentialDataset[0]

          let total: number = (dataset.data as number[]).reduce((sum, current) => sum + current, 0);
          let idx: number = labelsS.indexOf(dataset.label);
          cdataset.data[idx].value = total;
          cdataset.data[idx].feature = nations.filter((n) => {
            let ppays: IPays[] = pays.filter(p => p.payS_COUNTRY_ALPHA3 == n.properties.ADM0_A3_GB);
            if (ppays.length <= 0)
              return false;
            else
              return (ppays[0].codeLexique ?? ppays[0].payS_NAME_GB) == dataset.label
          })[0];
        }

        labelsS.forEach((value, index, array) => array[index] = this.translate.instant(value));
        labels = labelsS;
      }
      else if (isTree) {
        let browsingNodes: StatsGraphDataBrowsingNode[] = (this.content.config.specific.find(s => s.type == selectedType).options as any).rdata.data[""][""].browsingNodes;
        let abNodes: any[] = [];
        for (let browsingNode of browsingNodes) {
          abNodes.push({ name: this.translate.instant(browsingNode.name), parents: browsingNode.parents, value: browsingNode.value, color: browsingNode.alternateColor ? "#009236" : undefined, details: browsingNode.details });
        }

        let cdataset: ChartDataset2<any> = { label: '', key: '', data: abNodes };
        actualDatasets.push(cdataset)
        perDatasetColors = ["#0177BE"]; //Utils.makeColors([1], this.content.config.colorKind);
      }
      else {
        perDatasetColors = Utils.makeColors(new Array(this.content.config.data.datasets.length).fill(1), this.content.config.colorKind);
        actualDatasets = this.content.config.data.datasets;
        labels = this.content.config.data.labels;
      }

      let sets: ChartDataset[] = [];

      if (isMap)
        sets.push({
          label: "",
          data: [],
          outline: nations,
          showOutline: true,
          outlineBorderWidth: 1,
          outlineBorderColor: 'black'
        });

      for (let i = 0; i < actualDatasets.length; i++) {
        let dataset: ChartDataset2<any> = actualDatasets[i];

        let color = (isPieOrDonut || isMap) ? Utils.makeColors(isMap ? dataset.data.map(d => d.value) : dataset.data, this.content.config.colorKind) : perDatasetColors[i];

        sets.push({
          indexAxis: dataset.indexAxis,
          label: ((dataset.key != "") ? (this.translate.instant(dataset.key) + " - ") : "") + ((dataset.label != "") ? this.translate.instant(dataset.label) : ""),
          order: dataset.order,
          stack: dataset.stack,
          hidden: dataset.hidden,

          data: dataset.data,

          backgroundColor: color,
          borderWidth: dataset.borderWidth,
          borderColor: (isPieOrDonut || isTree) ? dataset.borderColor : color,
        });

        if (isMap) {
          (sets[sets.length - 1] as ChartDataset<'choropleth', (IGeoDataPoint & { value: number; })[]>).outline = nations;
          (sets[sets.length - 1] as ChartDataset<'choropleth', (IGeoDataPoint & { value: number; })[]>).showOutline = false;
        }
      }

      let options: ChartOptions = undefined;
      let plugins: Plugin[] = undefined;
      for (let i = 0; i < this.content.config.specific.length; i++) {
        if (selectedType == this.content.config.specific[i].type) {
          options = this.content.config.specific[i].options;
          plugins = this.content.config.specific[i].plugins;
          break;
        }
      }

      if (plugins == undefined)
        plugins = [];

      options = { ...options };

      options.maintainAspectRatio = false;

      options.interaction = {
        intersect: !this.largeSel,
        mode: (this.indexSel && !isMap) ? (isPieOrDonut ? 'dataset' : 'index') : 'nearest'
      };

      if (!isPieOrDonut && !isMap && !isTree) {
        if (options.scales == null || options.scales == undefined)
          options.scales = {};

        if (options.scales.x == null || options.scales.x == undefined)
          options.scales.x = {};

        if ((options.scales.x as any).title == null || (options.scales.x as any).title == undefined)
          (options.scales.x as any).title = {};

        if (options.scales.y == null || options.scales.y == undefined)
          options.scales.y = {};

        if ((options.scales.y as any).title == null || (options.scales.y as any).title == undefined)
          (options.scales.y as any).title = {};

        options.scales.x.display = true;
        (options.scales.x as any).title.display = true;
        (options.scales.x as any).title.text = this.translate.instant(this.content.config.xName);
        (options.scales.y as any).title.display = true;
        (options.scales.y as any).title.text = this.translate.instant(this.content.config.yName);
      }


      if (!isMap && !isTree) {
        plugins.push(this.htmlLegendPlugin);
        (options.plugins as any).htmlLegend = {
          component: this,
          id: -1
        };
      }
      else if(isMap) {
        if (options.plugins.tooltip == null || options.plugins.tooltip == undefined)
          options.plugins.tooltip = {};

        if (options.plugins.tooltip.callbacks == null || options.plugins.tooltip.callbacks == undefined)
          options.plugins.tooltip.callbacks = {};

        options.plugins.tooltip.callbacks.label = (context) => {
          return (context as any).dataset.label + this.translate.instant('stats.common.colon') + context.formattedValue;
        }

        options.plugins.tooltip.callbacks.title = (context) => {
          return context[0].chart.config.data.labels[context[0].dataIndex] as string;
        }
      }

      if (this.viewShape == StatsGraphShape.HIST) {
        if (options.datasets == null || options.datasets == undefined)
          options.datasets = {};

        (options.scales.x as any).stacked = this.stackBar;
        (options.scales.y as any).stacked = this.stackBar;
        (options.datasets as any).barPercentage = this.stackBar ? 1.0 : 0.9;
      }


      let setsContainers: ChartDataset[][] = [];
      if (isPieOrDonut) {
        for (let set of sets) {
          setsContainers.push([set]);
        }
      }
      else
        setsContainers = [sets];

      this.legendItemsL = new Array<LegendItemExp[]>(setsContainers.length).fill([]);
      this.charts = new Array<Chart>(setsContainers.length).fill(null);
      this.updateCanvasCount();

      let canvases: ElementRef[] = this.canvases.toArray();

      for (let i: number = 0; i < setsContainers.length; i++) {
        let toptions: ChartOptions = { ...options };
        toptions.plugins = { ...options.plugins };
        (toptions.plugins as any).htmlLegend = { ...(options.plugins as any).htmlLegend };

        let config: ChartConfiguration = {
          type: selectedType,
          data: {
            labels: labels,
            datasets: setsContainers[i]
          },
          options: toptions,
          plugins: plugins
        };


        if (isPieOrDonut || isMap) {
          config.options.plugins.title = {
            display: true,
            text: config.data.datasets[0].label
          };
        }

        if (!isMap && !isTree)
          (config.options.plugins as any).htmlLegend.id = i;

        this.charts[i] = new Chart((<HTMLCanvasElement>canvases[i].nativeElement).getContext('2d'), config);
      }

      this.updateShownLegendItems();
    }
    else {
      for (let chart of this.charts)
        if (chart != null)
          chart.destroy();

      this.charts = [];
    }
  }

  legendClick(clicked: LegendItem, update: boolean = true)
  {
    let legendItems: LegendItemExp[] = this.legendItemsL.reduce((accumulator, value) =>  accumulator.concat(value.filter(l => l.val.text == clicked.text)), []);

    for (let item of legendItems) {
      const type = (item.chart.config as any).type;
      if (type === 'pie' || type === 'doughnut') {
        // Pie and doughnut charts only have a single dataset and visibility is per item
        item.chart.toggleDataVisibility(item.val.index);
      } else {
        item.chart.setDatasetVisibility(item.val.datasetIndex, !item.chart.isDatasetVisible(item.val.datasetIndex));
      }

      if (update)
        item.chart.update();
    }

    if (update)
      this.updateShownLegendItems();
  }

  legendShowAll() {
    let list: LegendItem[] = this.legendItemsS;

    for (let item of list)
      if (item.hidden)
        this.legendClick(item, false);

    for(let chart of this.charts)
      chart.update();

    this.updateShownLegendItems();
  }

  legendShowNone() {
    let list: LegendItem[] = this.legendItemsS;

    for (let item of list)
      if (!item.hidden)
          this.legendClick(item, false);

    for (let chart of this.charts)
      chart.update();

    this.updateShownLegendItems();
  }

  updateShownLegendItems() {
    let list: LegendItem[] = [];

    for (let legendItem of this.legendItemsL)
      for (let item of legendItem)
        if (list.filter(l => l.text == item.val.text).length <= 0)
          list.push(item.val);

    this.legendItemsS = list;
  }

  htmlLegendPlugin = {
    id: 'htmlLegend',
    renderer: this.renderer,
    afterUpdate(chart: Chart, args, options) {
      // Reuse the built-in legendItems generator
      options.component.legendItemsL[options.id] = chart.options.plugins.legend.labels.generateLabels(chart).map(item => <LegendItemExp>{ val: item, chart: chart });
    }
  };

  treeScrollRight() {
    for (let chart of this.charts) {
      let ctrl = (chart.getDatasetMeta(0).controller as DendrogramController);
      let opt = (chart.options as IGraphChartOptions);
      if (!opt.shift)
        opt.shift = 0;

      if (opt.shift < ctrl.maxShift) {
        opt.shift++;
        ctrl.reLayout();
      }
    }
  }

  treeScrollLeft() {
    for (let chart of this.charts) {
      let ctrl = (chart.getDatasetMeta(0).controller as DendrogramController);
      let opt = (chart.options as IGraphChartOptions);
      if (!opt.shift)
        opt.shift = 0;

      else if (opt.shift > 0) {
        opt.shift--;
        ctrl.reLayout();
      }
    }
  }

  treeFullRight() {
    for (let chart of this.charts) {
      let ctrl = (chart.getDatasetMeta(0).controller as DendrogramController);
      let opt = (chart.options as IGraphChartOptions);
      opt.shift = ctrl.maxShift;
      ctrl.reLayout();
    }
  }

  treeFullLeft() {
    for (let chart of this.charts) {
      let ctrl = (chart.getDatasetMeta(0).controller as DendrogramController);
      let opt = (chart.options as IGraphChartOptions);
      opt.shift = 0;
      ctrl.reLayout();
    }
  }

  updateCanvasCount() {
    let tbl = new Array(this.charts.length);
    tbl.forEach((value, index, array) => array[index] = index);
    this.canvasTbl = tbl;
    this.cdRef.detectChanges();
  }

  sortTable(sort: Sort, status: string) {
    var data = this.tableDataSource.data;
    data.sort((a: any, b: any) => {
      return Constants.compare(a[sort.active], b[sort.active], sort.direction === 'asc');
    });
    this.tableDataSource.data = data.slice();
  }

  initDate(v: any): Date {
    return new Date(v);
  }

  isTranslateKey(key: any): boolean {
    if (typeof key !== 'string')
      return false;

    return (key.startsWith("stats."));
  }

  getExcelColumnName(columnNumber: number) : string {
    let columnName: string = "";

    while (columnNumber > 0) {
      let modulo: number = (columnNumber - 1) % 26;
      columnName = String.fromCharCode(65 + modulo) + columnName;
      columnNumber = Math.floor((columnNumber - modulo) / 26);
    }

    return columnName;
  }

  exportXlsx() {
    if (this.tableDataSource == null)
      return;

    let name = this.translate.instant(this.title);

    const options = {
      openAsDownload: true,
      filename: name,
      format: 'xlsx'
    } as ConvertOptions;

    let data: any[][] = [];
    let formats: FormatDefinition[] = [];

    let displayCols: { [key: string]: string } = {};
    if (this.viewShape == StatsGraphShape.ARBITRARYTABLE) {
      displayCols = this.displayedArbDict;
    }
    else if (this.viewShape == StatsGraphShape.TABLE || this.viewShape == StatsGraphShape.ACTIONS) {
      displayCols[this.displayedColumns[0]] = this.content.config.xName;
      displayCols[this.displayedColumns[1]] = this.content.config.keyName;
      displayCols[this.displayedColumns[2]] = this.content.config.labelName;
      displayCols[this.displayedColumns[3]] = this.content.config.yName;
    }

    let headers: string[] = [];
    let content: any[][] = [];

    let colKeys: string[] = [];
    let colLetters: string[] = [];
    for (let key in displayCols) {
      if (displayCols[key] !== '') {
        headers.push(this.translate.instant(displayCols[key]));
        colLetters.push(this.getExcelColumnName(headers.length));
        colKeys.push(key);
      }
    }

    let nbCol = colKeys.length;

    formats.push({
      "range": "A1:" + colLetters[colLetters.length-1] + "1",
      "format": ExcellentExport.formats.TEXT
    });

    for (let row of this.tableDataSource.data) {
      let r = [];

      for (let i = 0; i < nbCol; i++) {
        let elem = row[colKeys[i]];
        let cell = colLetters[i] + (content.length + 2);

        if (Utils.isDate(elem))
          elem = this.initDate(elem).toLocaleString();

        if (typeof (elem) === 'number') {
          formats.push({
            "range": cell,
            "format": (Number.isInteger(elem) && !colKeys[i].toLowerCase().includes('price')) ? ExcellentExport.formats.INTEGER : ExcellentExport.formats.DECIMAL
          });
        }
        else {
          formats.push({
            "range": cell,
            "format": ExcellentExport.formats.TEXT
          });
        }

        r.push(elem);
      }

      content.push(r);
    }

    data = [headers, ...content];

    const sheets = [
      {
        name: name,
        from: {
          array: data
        },
        formats: formats
      }
    ] as SheetOptions[];

    if (this.viewShape == StatsGraphShape.TABLE) {
      let valueIdx = colKeys.indexOf('value');
      if (valueIdx >= 0) {
        sheets[0].filterRowFn = (row) => {
          return row[valueIdx] != 0;
        };
      }
    }

    ExcellentExport.convert(options, sheets);
  }

  ngOnInit() {
    this.largeSel = false;
    this.indexSel = false;
    this.stackBar = false;

    this.translateSub = this.translate.onLangChange.subscribe(() => {
      this.chartChanged();
    });
  }

  ngOnDestroy() {
    if (this.translateSub != null) this.translateSub.unsubscribe();
  }

  changeLargeSel() {
    if (this.charts.length <= 0)
      return;

    for (let chart of this.charts) {
      if (chart == null)
        continue;

      chart.options.interaction.intersect = !this.largeSel;
      chart.update();
    }
  }

  changeIndexSel() {
    if (this.charts.length <= 0)
      return;

    for (let chart of this.charts) {
      if (chart == null)
        continue;

      const type = (chart.config as any).type;
      chart.options.interaction.mode = this.indexSel ? ((type === 'pie' || type === 'doughnut') ? 'dataset' : 'index') : 'nearest';
      chart.update();
    }
  }

  changeStackBar() {
    if (this.charts.length <= 0)
      return;

    for (let chart of this.charts) {
      if (chart == null)
        continue;

      chart.options.scales = {
        x: {
          stacked: this.stackBar,
        },
        y: {
          stacked: this.stackBar,
        }
      };

      chart.options.datasets.bar.barPercentage = this.stackBar ? 1.25 : 0.9;

      chart.update();
    }
  }
}
